/**
 * Client-side validation for the member checkout flow.
 *
 * Rules mirror the design: password policy is reported per rule, billing must be
 * a US state or territory, and optional fields never block progress.
 */

import type {
  AcceleratorDraft,
  AddressDraft,
  AgreementDraft,
  ContactDraft,
  FieldErrors,
  LoginDraft,
  PaymentDraft,
} from './types';

export interface PasswordRuleResult {
  id: string;
  label: string;
  met: boolean;
}

const PASSWORD_RULES: Array<{ id: string; label: string; test: (value: string) => boolean }> = [
  { id: 'length', label: 'At least 11 characters', test: (v) => v.length >= 11 },
  { id: 'upper', label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { id: 'lower', label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { id: 'number', label: 'One number', test: (v) => /\d/.test(v) },
  { id: 'symbol', label: 'One symbol', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export const evaluatePassword = (value: string): PasswordRuleResult[] =>
  PASSWORD_RULES.map(({ id, label, test }) => ({ id, label, met: test(value) }));

export const isPasswordValid = (value: string): boolean =>
  evaluatePassword(value).every((rule) => rule.met);

export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

/** US states, DC and the US territories that can be billed in USD. */
export const US_STATE_OPTIONS: Array<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'US Virgin Islands' },
];

const US_STATE_CODES = new Set(US_STATE_OPTIONS.map((option) => option.code));

export const isUsStateCode = (value: string): boolean => US_STATE_CODES.has(value.toUpperCase());

export const isValidZip = (value: string): boolean => /^\d{5}(-\d{4})?$/.test(value.trim());

export const isValidPhone = (value: string): boolean =>
  value.replace(/\D/g, '').length === 10;

export const validateLoginStep = (draft: LoginDraft): FieldErrors => {
  const errors: FieldErrors = {};

  if (!draft.email.trim()) errors.email = 'Email is required.';
  else if (!isValidEmail(draft.email))
    errors.email = 'Enter a valid email address (example: name@company.com).';

  if (!draft.firstName.trim()) errors.firstName = 'First name is required.';
  if (!draft.lastName.trim()) errors.lastName = 'Last name is required.';

  if (!draft.password) errors.password = 'Create a password to continue.';
  else if (!isPasswordValid(draft.password)) {
    const missing = evaluatePassword(draft.password).filter((rule) => !rule.met).length;
    errors.password = `Your password is missing ${missing} requirement${missing === 1 ? '' : 's'} — see below.`;
  }

  if (!draft.confirmPassword) errors.confirmPassword = 'Confirm your password to continue.';
  else if (draft.password !== draft.confirmPassword)
    errors.confirmPassword = "Passwords don't match. Re-enter to confirm.";

  return errors;
};

const validateAddress = (address: AddressDraft, prefix: string, requireUs: boolean): FieldErrors => {
  const errors: FieldErrors = {};

  if (!address.street.trim()) errors[`${prefix}.street`] = 'Street address is required.';
  if (!address.city.trim()) errors[`${prefix}.city`] = 'City is required.';

  if (!address.state) errors[`${prefix}.state`] = 'Required';
  else if (requireUs && !isUsStateCode(address.state))
    errors[`${prefix}.state`] = 'Select a US state or territory.';

  if (!address.zip.trim()) errors[`${prefix}.zip`] = 'ZIP code is required.';
  else if (!isValidZip(address.zip)) errors[`${prefix}.zip`] = 'Enter a 5-digit ZIP';

  return errors;
};

export const validateContactStep = (draft: ContactDraft): FieldErrors => {
  const errors: FieldErrors = {
    ...validateAddress(draft.billing, 'billing', true),
  };

  if (!draft.legalName.trim()) errors.legalName = 'Full legal name is required.';

  if (!draft.mailingSameAsBilling) {
    Object.assign(errors, validateAddress(draft.mailing, 'mailing', false));
  }

  if (!draft.phone.trim()) errors.phone = 'Phone is required.';
  else if (!isValidPhone(draft.phone)) errors.phone = 'Enter a 10-digit US phone number.';

  if (!draft.agencyName.trim()) errors.agencyName = 'Travel agency name is required.';

  if (!draft.hasAssociates) errors.hasAssociates = 'Select Yes or No to continue.';
  else if (draft.hasAssociates === 'yes') {
    const count = Number(draft.associateCount);
    if (!draft.associateCount.trim()) errors.associateCount = 'Enter how many associate advisors you have.';
    else if (!Number.isInteger(count) || count < 1)
      errors.associateCount = 'Enter a whole number of 1 or more.';
  }

  // `referredBy` is optional — never an error.
  return errors;
};

export const validateAcceleratorStep = (draft: AcceleratorDraft): FieldErrors => {
  const errors: FieldErrors = {};
  if (!draft.added) return errors;

  if (draft.cohortIds.length === 0) errors.cohortIds = 'Select at least one date.';
  if (!draft.format) errors.format = 'Choose virtual or in person.';

  return errors;
};

const luhnValid = (digits: string): boolean => {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let value = Number(digits[i]);
    if (double) {
      value *= 2;
      if (value > 9) value -= 9;
    }
    sum += value;
    double = !double;
  }
  // PANs are 13-19 digits.
  return digits.length >= 13 && digits.length <= 19 && sum % 10 === 0;
};

/** True when an `MM/YY` expiry is this month or later. */
const isFutureExpiry = (expiry: string): boolean => {
  const [month, year] = expiry.split('/').map(Number);
  const now = new Date();
  const currentYear = now.getFullYear() % 100;
  const currentMonth = now.getMonth() + 1;
  return year > currentYear || (year === currentYear && month >= currentMonth);
};

export const validatePaymentStep = (draft: PaymentDraft): FieldErrors => {
  const errors: FieldErrors = {};
  const digits = draft.cardNumber.replace(/\D/g, '');

  if (!digits) errors.cardNumber = 'Card number is required.';
  else if (!luhnValid(digits)) errors.cardNumber = 'Check the card number and try again.';

  const expiry = draft.expiry.replace(/\s/g, '');
  if (!expiry) errors.expiry = 'Expiration is required.';
  else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry)) errors.expiry = 'Use MM / YY.';
  else if (!isFutureExpiry(expiry)) errors.expiry = 'This card has expired.';

  if (!draft.cvc.trim()) errors.cvc = 'CVC is required.';
  else if (!/^\d{3,4}$/.test(draft.cvc.trim())) errors.cvc = 'Enter 3 or 4 digits.';

  if (!draft.zip.trim()) errors.zip = 'ZIP code is required.';
  else if (!isValidZip(draft.zip)) errors.zip = 'Enter a 5-digit ZIP';

  return errors;
};

export const validateAgreementStep = (draft: AgreementDraft): FieldErrors =>
  draft.termsAccepted
    ? {}
    : { termsAccepted: 'You must accept the Membership Terms of Service to continue.' };

export const errorCount = (errors: FieldErrors): number => Object.keys(errors).length;
